import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, MergeType } from '@mui/icons-material';
import { accountsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { formatCurrency, formatDate } from '../utils/helpers';
import LoadingButton from './LoadingButton';

export const ACCOUNT_KINDS = [
  { value: 'bank', label: 'Bank account' },
  { value: 'credit_card', label: 'Credit card' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'loan', label: 'Loan' },
  { value: 'investment', label: 'Investment account' },
  { value: 'cash', label: 'Cash' },
];
const kindLabel = (kind) => ACCOUNT_KINDS.find(k => k.value === kind)?.label || kind;

const EMPTY_FORM = {
  name: '', kind: 'bank', ownerLabel: '', openingBalance: '', openingDate: '',
  creditLimit: '', statementDay: '', dueDay: '', isActive: true,
};

/**
 * Settings > Accounts: the accounts found in statements and emails (one per
 * bank account or card), editable, and mergeable when one account shows up
 * under two numbers.
 */
const AccountsManager = () => {
  const { showSuccess, showError } = useNotification();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, account: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [mergeDialog, setMergeDialog] = useState({ open: false, account: null, intoAccountId: '' });
  const [merging, setMerging] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await accountsApi.getAll();
      setAccounts(response.data.accounts);
    } catch (error) {
      showError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (account) => {
    setForm(account ? {
      name: account.name || '',
      kind: account.kind,
      ownerLabel: account.ownerLabel || '',
      openingBalance: account.openingBalance ?? '',
      openingDate: account.openingDate || '',
      creditLimit: account.creditLimit ?? '',
      statementDay: account.statementDay ?? '',
      dueDay: account.dueDay ?? '',
      isActive: account.isActive,
    } : EMPTY_FORM);
    setEditDialog({ open: true, account });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...form };
      if (data.kind !== 'credit_card') {
        data.creditLimit = null;
        data.statementDay = null;
        data.dueDay = null;
      }
      if (editDialog.account) {
        await accountsApi.update(editDialog.account.id, data);
        showSuccess('Account saved');
      } else {
        await accountsApi.create(data);
        showSuccess('Account added');
      }
      setEditDialog({ open: false, account: null });
      load();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      const response = await accountsApi.merge(mergeDialog.account.id, mergeDialog.intoAccountId);
      showSuccess(response.data.message);
      setMergeDialog({ open: false, account: null, intoAccountId: '' });
      load();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to merge accounts');
    } finally {
      setMerging(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
  });

  return (
    <Box>
      <Card>
        <CardHeader
          title="Accounts"
          subheader="Accounts and cards are added automatically from your statements and emails"
          action={
            <Button variant="contained" startIcon={<Add />} onClick={() => openEdit(null)}>
              Add Account
            </Button>
          }
        />
        <CardContent>
          {accounts.length === 0 ? (
            <Alert severity="info">
              No accounts yet. They appear here once you upload a statement or forward a bank email.
            </Alert>
          ) : (
            <List disablePadding>
              {accounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem sx={{ pr: 12 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body1">{account.name}</Typography>
                          <Chip label={kindLabel(account.kind)} size="small" variant="outlined" />
                          {account.ownerLabel && <Chip label={account.ownerLabel} size="small" variant="outlined" />}
                          {!account.isActive && <Chip label="Closed" size="small" />}
                        </Stack>
                      }
                      secondary={[
                        account.numbers.length > 0 && `Numbers: ${account.numbers.map(n => `xx${n}`).join(', ')}`,
                        `${account.transactionCount} transaction${account.transactionCount === 1 ? '' : 's'}`,
                        account.lastTransactionDate && `last on ${formatDate(account.lastTransactionDate)}`,
                        account.latestBalance !== null && `balance ${formatCurrency(account.latestBalance)} on ${formatDate(account.latestBalanceDate)}`,
                      ].filter(Boolean).join(' · ')}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(account)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Merge into another account">
                        <span>
                          <IconButton
                            size="small"
                            disabled={accounts.length < 2}
                            onClick={() => setMergeDialog({ open: true, account, intoAccountId: '' })}
                          >
                            <MergeType />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, account: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{editDialog.account ? 'Edit Account' : 'Add Account'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required fullWidth {...field('name')} />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" {...field('kind')}>
                {ACCOUNT_KINDS.map(k => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Whose account"
              placeholder="e.g. Mine, Joint, Partner"
              fullWidth
              {...field('ownerLabel')}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField label="Opening balance" type="number" fullWidth {...field('openingBalance')} />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="As of"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...field('openingDate')}
                />
              </Grid>
            </Grid>
            {form.kind === 'credit_card' && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField label="Credit limit" type="number" fullWidth {...field('creditLimit')} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField label="Statement day" type="number" inputProps={{ min: 1, max: 31 }} fullWidth {...field('statementDay')} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField label="Due day" type="number" inputProps={{ min: 1, max: 31 }} fullWidth {...field('dueDay')} />
                </Grid>
              </Grid>
            )}
            {editDialog.account && (
              <FormControlLabel
                control={<Switch checked={!form.isActive} onChange={(e) => setForm({ ...form, isActive: !e.target.checked })} />}
                label="Closed (no longer used)"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, account: null })}>Cancel</Button>
          <LoadingButton variant="contained" onClick={handleSave} loading={saving} disabled={!form.name.trim()}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={mergeDialog.open} onClose={() => setMergeDialog({ open: false, account: null, intoAccountId: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Merge "{mergeDialog.account?.name}"</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Use this when one account or card shows up twice (for example under its card number and its
              account number). Its numbers, transactions, balances and bills move to the account you choose,
              and "{mergeDialog.account?.name}" is removed.
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Merge into</InputLabel>
              <Select
                label="Merge into"
                value={mergeDialog.intoAccountId}
                onChange={(e) => setMergeDialog({ ...mergeDialog, intoAccountId: e.target.value })}
              >
                {accounts.filter(a => a.id !== mergeDialog.account?.id).map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.name} ({kindLabel(a.kind)})</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeDialog({ open: false, account: null, intoAccountId: '' })}>Cancel</Button>
          <LoadingButton variant="contained" onClick={handleMerge} loading={merging} disabled={!mergeDialog.intoAccountId}>
            Merge
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsManager;
